import { Navigate, useLocation } from 'react-router-dom';
import { useBootup } from '@/hooks/useBootup';
import { LoadingPage } from '@/components/screens/LoadingPage';
import { ResultsPending } from '@/components/shared/ResultsPending';
import { HomePage } from '@screens/HomePage/HomePage';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { getLottieFilesFromCdn } from '@/utils/nutrition/getImageUrl';
import { CancerDetectPage } from '@screens/CancerDetectPage';
import { NoActiveKitsPage } from '@screens/ErrorHandling/NoActiveKitsPage';
import { useIframeModeStore } from '@/hooks/useIframeModeStore';
import { useMustSelectContext } from '@/hooks/context/useContextStore';
import { useContextResolver } from '@/hooks/context/useContextResolver';
import { EXPERIENCE_PICKER_PATH } from '@/constants/paths';

export interface BootupGuardProps {
  children?: React.ReactNode;
}

export const BootupGuard = ({ children }: BootupGuardProps) => {
  const { isIframeMode } = useIframeModeStore();

  if (isIframeMode) {
    return children;
  }

  return <FullBootupGuard>{children}</FullBootupGuard>;
};

const FullBootupGuard = ({ children }: BootupGuardProps) => {
  // Hydrate context + resolve activeKitId for EVERY authenticated session
  // (already-logged-in users, fresh tabs, deep-links) — not just via the picker.
  // Without this, kit-scoped v2 queries stay disabled (null activeKitId).
  const { isReady: isContextReady } = useContextResolver();
  const mustSelectContext = useMustSelectContext();
  const { isLoading, isError, bootupState } = useBootup();
  const { kitDetailsData, CDkitDetailsData } = bootupState || {};
  const hideSideBarOptionsForCancerDetectUsers = CDkitDetailsData && !kitDetailsData;
  const isNoKitDetailsAvailable = (obj: unknown) => !obj || Object.keys(obj as object).length === 0;

  const location = useLocation();
  const locationPathName = location?.pathname;
  const nutritionLottieAnimate = getLottieFilesFromCdn(
    'coming_soon_light.json',
  );
  const healthLottieAnimate = getLottieFilesFromCdn('coming_soon_dark.json');

  // Wait for context hydration + activeKitId resolution before rendering, so
  // home/questionnaire/etc. never mount with a null kit (kit-scoped v2 queries).
  // Errors are non-fatal here — they fall through to useBootup's v1 fallback.
  if (!isContextReady) {
    return <LoadingPage />;
  }

  // Picker lock (DE-336): when both experiences exist and the user hasn't
  // chosen yet, no app route is accessible — bounce back to the picker until
  // they pick a context (consumer or an org).
  if (mustSelectContext) {
    return <Navigate to={EXPERIENCE_PICKER_PATH} replace />;
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError) {
    return <ErrorLoadingPage errorType='logout' />;
  }

  if (isNoKitDetailsAvailable(kitDetailsData) && isNoKitDetailsAvailable(CDkitDetailsData)) {
    return <NoActiveKitsPage type='nokitregister' />;
  }

  if (
    !bootupState?.recommendationExists &&
    locationPathName?.includes('/nutrition')
  ) {
    return (
      <ResultsPending
        title={
          "When your nutrition recommendations are ready, you'll find them here."
        }
        videoSrc={nutritionLottieAnimate}
      />
    );
  }

  if (
    !bootupState?.recommendationExists &&
    locationPathName?.includes('/health')
  ) {
    return (
      <ResultsPending
        title={
          "When your health recommendations are ready, you'll find them here."
        }
        videoSrc={healthLottieAnimate}
        dark
        className="dark"
      />
    );
  }

  if (!bootupState?.recommendationExists && locationPathName === '/' && !hideSideBarOptionsForCancerDetectUsers) {
    return <HomePage />;
  }

  if (locationPathName === '/' && hideSideBarOptionsForCancerDetectUsers) {
    return <CancerDetectPage />;
  }

  return children;
};
